/* ------------------------------------------------------------------ */
/*  Dashboard.jsx                                                     */
/* ------------------------------------------------------------------ */
import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  IconButton,
  Chip,
  Snackbar,
  CircularProgress,
  Fade,
  Button,
  useTheme
} from '@mui/material';
import {
  Refresh,
  Dashboard as DashboardIcon,
  Security,
  People,
  BusinessCenter,
  PersonAdd,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';
import { hasRouteAccess } from '../../utils/routeUtils';
import { USER_ROLES } from '../../utils/constants';
import {
  StatsCards,
  FiltersBar,
  RevenueChart,
  Leaderboards
} from '../../components/dashboard';

/* ------------------------------------------------------------------ */
/*  Role-specific metadata                                            */
/* ------------------------------------------------------------------ */
const ROLE_META = {
  [USER_ROLES.SUPER_ADMIN]:    { label: 'Super Admin',      icon: Security,      chip: 'warning' },
  [USER_ROLES.ADMIN]:          { label: 'Admin',            icon: People,        chip: 'primary' },
  [USER_ROLES.ADMIN_VIEW]:     { label: 'Admin View',       icon: People,        chip: 'info' },
  [USER_ROLES.NLA_ADMIN]:      { label: 'System Admin',     icon: Security,      chip: 'warning' },
  [USER_ROLES.STATION_MANAGER]:{ label: 'Station Manager',  icon: BusinessCenter,chip: 'secondary' },
  [USER_ROLES.INFLUENCER]:     { label: 'Influencer',       icon: DashboardIcon, chip: 'info' },
  [USER_ROLES.PRESENTER]:      { label: 'Presenter',        icon: DashboardIcon, chip: 'default' }
};

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
const Dashboard = () => {
  const theme   = useTheme();
  const nav     = useNavigate();
  const { user, role } = useAuth();

  /*  Dashboard slice state  */
  const { loading, error } = useSelector(s => s.dashboard);

  /*  Local snackbar control  */
  const [openSnack, setOpenSnack] = useState(Boolean(error));
  const closeSnack  = () => setOpenSnack(false);

  /*  Global refresh handler – RTK-Query tag-based refetch  */
  const handleRefresh = useCallback(() => {
    // dispatch(invalidateTags(['Dashboard']));  <-- if using RTK-Query
    // fallback:
    window.location.reload();
  }, []);

  const { icon: RoleIcon, chip: chipColor, label: roleLabel } =
    ROLE_META[role] || { icon: DashboardIcon, chip: 'default', label: 'User' };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header ---------------------------------------------------- */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          backdropFilter: 'blur(8px)',
          background: theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(255,255,255,0.7)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <RoleIcon color="action" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {`Welcome back, ${user?.username ?? 'User'}!`}
            </Typography>
            <Chip
              size="small"
              color={chipColor}
              label={roleLabel}
              sx={{ mt: 0.5, fontWeight: 600 }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {role === USER_ROLES.SUPER_ADMIN && (
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => nav('/users?create=1')}
            >
              Add User
            </Button>
          )}
          <IconButton
          onClick={handleRefresh}
          disabled={loading}
          sx={{ position: 'relative' }}
          aria-label="refresh dashboard"
        >
          <Refresh />
          {loading && (
            <CircularProgress
              size={40}
              sx={{
                position: 'absolute',
                top: -6,
                left: -6,
                zIndex: 1,
                opacity: 0.6
              }}
            />
          )}
        </IconButton>
        </Box>
      </Box>

      {/* Global Filters ------------------------------------------- */}
      <FiltersBar />

      {/* Stats Cards - outside Grid for proper full-width on mobile */}
      <Box sx={{ mb: 3 }}>
        <StatsCards />
      </Box>

      {/* Main Grid ------------------------------------------------- */}
      <Grid container spacing={3}>
        {/* {hasRouteAccess(role, '/deposits') && ( */}
          <Grid item xs={12}>
            <RevenueChart />
          </Grid>
        {/* )} */}

        {/* {hasRouteAccess(role, '/customers') && ( */}
          <Grid item xs={12} md={12}>
            <Leaderboards />
          </Grid>
        {/* )} */}
      </Grid>

      {/* Error snackbar ------------------------------------------- */}
      <Snackbar
        open={openSnack}
        onClose={closeSnack}
        message={error}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      />
    </Container>
  );
};

export default Dashboard;


// import React from 'react';
// import {
//   Box,
//   Typography,
//   Container,
//   Alert,
//   useTheme,
//   Grid,
//   Card,
//   CardContent,
//   Button,
//   IconButton,
//   Chip
// } from '@mui/material';
// import {
//   Refresh,
//   Dashboard as DashboardIcon,
//   Security,
//   People,
//   BusinessCenter
// } from '@mui/icons-material';
// import { useSelector, useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../../hooks/useAuth';
// import { hasRouteAccess } from '../../utils/routeUtils';
// import { USER_ROLES } from '../../utils/constants';
// import { StatsCards, FiltersBar, RevenueChart, Leaderboards } from '../../components/dashboard';

// // Helper object for role-specific UI elements
// const roleInfoMap = {
//   [USER_ROLES.ADMIN]: {
//     welcome: 'Administrative Control',
//     icon: People,
//   },
//   [USER_ROLES.NLA_ADMIN]: {
//     welcome: 'System Administration',
//     icon: Security,
//   },
//   [USER_ROLES.STATION_MANAGER]: {
//     welcome: 'Station Management Hub',
//     icon: BusinessCenter,
//   },
//   [USER_ROLES.INFLUENCER]: {
//     welcome: 'Influencer Dashboard',
//     icon: DashboardIcon,
//   },
//   [USER_ROLES.PRESENTER]: {
//     welcome: 'Presenter Dashboard',
//     icon: DashboardIcon,
//   }
// };

// const Dashboard = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const { error, loading } = useSelector((state) => state.dashboard);
//   const { user, role } = useAuth();

//   const roleInfo = roleInfoMap[role] || { welcome: 'Dashboard', icon: DashboardIcon };
//   const DynamicIcon = roleInfo.icon;

//   const handleRefresh = () => {
//     // Add your data refetching logic here, e.g., from RTK Query
//   };

//   const handleCloseError = () => {
//     // dispatch(setError(null));
//   };

//   return (
//     <Container maxWidth="xl" sx={{ py: 3 }}>
//       {/* Header */}
//       <Box sx={{ mb: 4 }}>
//         <Grid container spacing={2} justifyContent="space-between" alignItems="center">
//           <Grid item xs={12} md="auto">
//             <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
//               {roleInfo.welcome}
//             </Typography>
//             <Typography color="text.secondary">
//               Welcome back, {user?.username || 'User'}!
//             </Typography>
//           </Grid>
//           <Grid item xs={12} md="auto">
//             <IconButton onClick={handleRefresh} disabled={loading} color="primary">
//               <Refresh />
//             </IconButton>
//           </Grid>
//         </Grid>
//       </Box>

//       {/* Error Alert */}
//       {error && (
//         <Alert severity="error" onClose={handleCloseError} sx={{ mb: 3 }}>
//           {error}
//         </Alert>
//       )}

//       {/* Filters Bar */}
//       <FiltersBar />

//       {/* Main Content Grid */}
//       <Grid container spacing={3}>
//         {/* Stats Cards */}
//         <Grid item xs={12}>
//           <StatsCards />
//         </Grid>

//         {/* Revenue Chart */}
//         {hasRouteAccess(role, '/deposits') && (
//           <Grid item xs={12}>
//             <RevenueChart />
//           </Grid>
//         )}

//         {/* Leaderboards */}
//         {hasRouteAccess(role, '/customers') && (
//           <Grid item xs={12} md={6}>
//             <Leaderboards />
//           </Grid>
//         )}

//         {/* Admin-Only Panel */}
//         {role === USER_ROLES.ADMIN && (
//           <Grid item xs={12} md={6}>
//             <Card>
//               <CardContent>
//                 <Typography variant="h6">Admin Tools</Typography>
//                 <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
//                   You have administrative access to system settings and user management.
//                 </Typography>
//                 <Box sx={{ display: 'flex', gap: 2 }}>
//                   <Button variant="contained" onClick={() => navigate('/users')}>Manage Users</Button>
//                   <Button variant="outlined" onClick={() => navigate('/admin/settings')}>System Settings</Button>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         )}
//       </Grid>
//     </Container>
//   );
// };

// export default Dashboard;

// // // // src/pages/Dashboard/Dashboard.jsx

// // src/pages/Dashboard/Dashboard.jsx
// import React, { useEffect } from 'react';
// import { 
//   Box, 
//   Typography, 
//   Container, 
//   Alert,
//   useTheme,
//   useMediaQuery,
//   Fade,
//   alpha,
//   Grid,
//   Paper,
//   IconButton,
//   Chip,
//   Card,
//   CardContent,
//   Button
// } from '@mui/material';
// import { 
//   Refresh, 
//   Dashboard as DashboardIcon,
//   Analytics,
//   TrendingUp,
//   Security,
//   People,
//   BusinessCenter
// } from '@mui/icons-material';
// import { useSelector, useDispatch } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import { useGetMetricsQuery } from '../../store/api/dashboardApi';
// import { setError } from '../../store/slices/dashboardSlice';
// import { useNavigation } from '../../hooks/useNavigation';
// import {
//   StatsCards,
//   FiltersBar,
//   RevenueChart,
//   Leaderboards,
// } from '../../components/dashboard';
// import RoleBasedWelcome from '../../components/dashboard/RoleBasedWelcome';

// const Dashboard = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { filters, error, loading } = useSelector((state) => state.dashboard);
//   const { 
//     roleInfo, 
//     user, 
//     quickAccessItems, 
//     navigationStats, 
//     canAccess 
//   } = useNavigation();
  
//   // Initial data fetch
//   const { refetch } = useGetMetricsQuery(filters);

//   const handleRefresh = () => {
//     refetch();
//   };

//   const handleCloseError = () => {
//     dispatch(setError(null));
//   };

//   // Role-based dashboard customization
//   const getDashboardIcon = () => {
//     switch (roleInfo?.label) {
//       case 'NLA Admin':
//         return Security;
//       case 'Administrator':
//         return People;
//       case 'Station Manager':
//         return BusinessCenter;
//       default:
//         return DashboardIcon;
//     }
//   };

//   const DynamicIcon = getDashboardIcon();

//   return (
//     <Box
//       sx={{
//         minHeight: '100vh',
//         background: theme.palette.mode === 'dark'
//           ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
//           : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
//         position: 'relative',
//         '&::before': {
//           content: '""',
//           position: 'fixed',
//           top: 0,
//           left: 0,
//           right: 0,
//           bottom: 0,
//           background: theme.palette.mode === 'dark'
//             ? 'radial-gradient(circle at 30% 30%, rgba(63, 187, 133, 0.1) 0%, transparent 50%)'
//             : 'radial-gradient(circle at 70% 20%, rgba(63, 187, 133, 0.05) 0%, transparent 50%)',
//           pointerEvents: 'none',
//           zIndex: 0,
//         }
//       }}
//     >
//       <Container 
//         maxWidth={false} 
//         sx={{ 
//           position: 'relative', 
//           zIndex: 1,
//           px: { xs: 2, sm: 3, md: 4 },
//           py: { xs: 2, sm: 3 }
//         }}
//       >
//         {/* Hero Header Section with Role-Based Customization */}
//         <Fade in timeout={500}>
//           <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
//             <Grid container spacing={3} alignItems="center">
//               <Grid item xs={12} md={8}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
//                   <Box
//                     sx={{
//                       p: 2,
//                       borderRadius: 3,
//                       background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
//                       display: 'flex',
//                       alignItems: 'center',
//                       justifyContent: 'center',
//                       boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
//                     }}
//                   >
//                     <DynamicIcon sx={{ color: 'white', fontSize: '2rem' }} />
//                   </Box>
//                   <Box>
//                     <Typography 
//                       variant="h3" 
//                       component="h1" 
//                       sx={{ 
//                         fontWeight: 900,
//                         background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
//                         backgroundClip: 'text',
//                         WebkitBackgroundClip: 'text',
//                         WebkitTextFillColor: 'transparent',
//                         fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
//                         letterSpacing: '-0.02em',
//                         lineHeight: 1.1,
//                       }}
//                     >
//                       {roleInfo?.welcome || 'Dashboard'}
//                     </Typography>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
//                       <Typography 
//                         variant="h6" 
//                         color="text.secondary"
//                         sx={{ 
//                           fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
//                           fontWeight: 500,
//                         }}
//                       >
//                         Welcome back, {user?.username || 'User'}
//                       </Typography>
//                       {roleInfo && (
//                         <Chip
//                           label={roleInfo.label}
//                           color={roleInfo.color}
//                           size="small"
//                           variant="outlined"
//                           sx={{ ml: 1 }}
//                         />
//                       )}
//                     </Box>
//                   </Box>
//                 </Box>

//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                     <Analytics sx={{ color: 'primary.main', fontSize: '1.25rem' }} />
//                     <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
//                       Live Data
//                     </Typography>
//                   </Box>
//                   <Box
//                     sx={{
//                       width: 8,
//                       height: 8,
//                       borderRadius: '50%',
//                       background: '#4CAF50',
//                       animation: 'pulse 2s infinite',
//                       '@keyframes pulse': {
//                         '0%': { opacity: 1, transform: 'scale(1)' },
//                         '50%': { opacity: 0.7, transform: 'scale(1.2)' },
//                         '100%': { opacity: 1, transform: 'scale(1)' },
//                       },
//                     }}
//                   />
//                   <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
//                     System Online
//                   </Typography>
                  
//                   {/* Role-based access indicator */}
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
//                     <Typography variant="caption" color="text.secondary">
//                       Access Level:
//                     </Typography>
//                     <Chip
//                       label={`${navigationStats?.totalItems || 0} sections`}
//                       size="small"
//                       color="info"
//                       variant="filled"
//                     />
//                   </Box>
//                 </Box>
//               </Grid>

//               <Grid item xs={12} md={4}>
//                 <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 2 }}>
//                   <IconButton
//                     onClick={handleRefresh}
//                     disabled={loading}
//                     sx={{
//                       background: alpha(theme.palette.primary.main, 0.1),
//                       border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
//                       color: 'primary.main',
//                       '&:hover': {
//                         background: alpha(theme.palette.primary.main, 0.2),
//                         transform: 'rotate(180deg)',
//                       },
//                       transition: 'all 0.3s ease',
//                       width: 48,
//                       height: 48,
//                     }}
//                   >
//                     <Refresh sx={{ fontSize: '1.25rem' }} />
//                   </IconButton>
//                 </Box>
//               </Grid>
//             </Grid>
//           </Box>
//         </Fade>

//         {/* Error Alert */}
//         {error && (
//           <Fade in timeout={300}>
//             <Alert 
//               severity="error" 
//               onClose={handleCloseError}
//               sx={{ 
//                 mb: 4,
//                 borderRadius: 2,
//                 border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
//                 background: alpha(theme.palette.error.main, 0.05),
//               }}
//             >
//               {error}
//             </Alert>
//           </Fade>
//         )}

    

       

//         {/* Filters Bar */}
//         <Box sx={{ mb: { xs: 3, sm: 4 } }}>
//           <FiltersBar />
//         </Box>

//         {/* Stats Cards */}
//         <Box sx={{ mb: { xs: 4, sm: 5 } }}>
//           <StatsCards />
//         </Box>

//         {/* Revenue Chart - Now Full Width with Role-Based Visibility */}
//         {(canAccess('/deposits') || canAccess('/withdrawals') || canAccess('/games')) && (
//           <Box sx={{ mb: { xs: 4, sm: 5 } }}>
//             <RevenueChart />
//           </Box>
//         )}

//         {/* Leaderboards Section - Role-Based Visibility */}
//         {(canAccess('/customers') || canAccess('/users')) && (
//           <Box sx={{ mt: { xs: 4, sm: 5, md: 6 } }}>
//             <Leaderboards />
//           </Box>
//         )}

       

//         {/* Admin-Only System Health Section */}
//         {canAccess('/admin') && (
//           <Fade in timeout={1200}>
//             <Card sx={{ mt: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
//               <CardContent sx={{ p: 3 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
//                   <Security sx={{ color: 'warning.main' }} />
//                   <Typography variant="h6" sx={{ fontWeight: 700 }}>
//                     System Administration
//                   </Typography>
//                 </Box>
//                 <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
//                   You have administrative access to system settings and user management.
//                 </Typography>
//                 <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
//                   <Button
//                     variant="contained"
//                     color="warning"
//                     startIcon={<People />}
//                     onClick={() => navigate('/users')}
//                     size="small"
//                   >
//                     Manage Users
//                   </Button>
//                   <Button
//                     variant="outlined"
//                     color="warning"
//                     startIcon={<Security />}
//                     onClick={() => navigate('/admin/settings')}
//                     size="small"
//                   >
//                     System Settings
//                   </Button>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Fade>
//         )}

//         {/* Footer Spacing */}
//         <Box sx={{ height: { xs: 60, sm: 80 } }} />
//       </Container>
//     </Box>
//   );
// };

// export default Dashboard;
